import { create } from 'zustand'
import { persist } from 'zustand/middleware'

const VALID_THEME_PREFERENCES = new Set(['light', 'system', 'dark'])
export const APP_THEME_STORAGE_KEY = 'wefa_app_preferences'

export type ThemePreference = 'light' | 'system' | 'dark'
export type ResolvedTheme = 'light' | 'dark'

interface AppPreferencesState {
  themePreference: ThemePreference
  setThemePreference: (themePreference: ThemePreference) => void
}

function isValidThemePreference(value: unknown): value is ThemePreference {
  return typeof value === 'string' && VALID_THEME_PREFERENCES.has(value)
}

export function resolveThemePreference(
  themePreference: ThemePreference,
  systemTheme: ResolvedTheme,
): ResolvedTheme {
  return themePreference === 'system' ? systemTheme : themePreference
}

export const useAppPreferencesStore = create<AppPreferencesState>()(
  persist(
    (set) => ({
      themePreference: 'system',
      setThemePreference: (themePreference) => set({ themePreference }),
    }),
    {
      name: APP_THEME_STORAGE_KEY,
      partialize: (state) => ({
        themePreference: state.themePreference,
      }),
      onRehydrateStorage: () => (state) => {
        if (!state) return
        if (!isValidThemePreference(state.themePreference)) {
          state.themePreference = 'system'
          try {
            window.localStorage.setItem(
              APP_THEME_STORAGE_KEY,
              JSON.stringify({
                state: { themePreference: 'system' },
                version: 0,
              }),
            )
          } catch {
            // Ignore storage write failures and keep the in-memory fallback.
          }
        }
      },
    },
  ),
)
